import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { ToastMessage } from "../components/ToastMessage";
import { ApiError } from "../types/errors";
import { ErrorHandlerService } from "../services/error-handler.service";
import { globalErrorToastService } from "../services/global-error-toast.service";

interface Toast {
  id: string;
  message: string;
  type: "error" | "warning" | "info";
  duration?: number;
}

interface ToastMessageContextType {
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  showError: (error: ApiError | string) => void;
}

const ToastMessageContext = createContext<ToastMessageContextType | undefined>(
  undefined,
);

interface ToastMessageProviderProps {
  children: React.ReactNode;
}

export const ToastMessageProvider: React.FC<ToastMessageProviderProps> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showError = useCallback((error: ApiError | string): void => {
    const message =
      typeof error === "string"
        ? error
        : ErrorHandlerService.getUserMessage(error);

    const toast: Toast = {
      id: `toast-${Date.now()}-${Math.random()}`,
      message,
      type: "error",
      duration: 5000,
    };

    setToasts((prev: Toast[]): Toast[] => [...prev, toast]);
  }, []);

  const showWarning = useCallback((message: string): void => {
    const toast: Toast = {
      id: `toast-${Date.now()}-${Math.random()}`,
      message,
      type: "warning",
      duration: 4000,
    };

    setToasts((prev: Toast[]): Toast[] => [...prev, toast]);
  }, []);

  const showInfo = useCallback((message: string): void => {
    const toast: Toast = {
      id: `toast-${Date.now()}-${Math.random()}`,
      message,
      type: "info",
      duration: 3000,
    };

    setToasts((prev: Toast[]): Toast[] => [...prev, toast]);
  }, []);

  // Initialize global error toast service when provider mounts
  useEffect((): void => {
    globalErrorToastService.initialize(showError);
  }, [showError]);

  return (
    <ToastMessageContext.Provider value={{ showError, showWarning, showInfo }}>
      {children}
      <div className="error-toast-container">
        {toasts.map((toast: Toast) => (
          <ToastMessage
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={(): void => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastMessageContext.Provider>
  );
};

export const useToastMessage = (): ToastMessageContextType => {
  const context: ToastMessageContextType | undefined =
    useContext(ToastMessageContext);
  if (context === undefined) {
    throw new Error("useErrorToast must be used within an ErrorToastProvider");
  }
  return context;
};
